import { Component, createSignal, createEffect, Show } from 'solid-js';
import { marked } from 'marked';
import { useLanguage } from '../../i18n/LanguageContext';
import { useNavigation } from '../../contexts/NavigationContext';

interface BlogPostProps {
  slug: string;
}

interface Post {
  title: string;
  date: string;
  content: string;
}

const BlogPost: Component<BlogPostProps> = (props) => {
  const { t, language } = useLanguage();
  const { navigateToBlog } = useNavigation();
  const [post, setPost] = createSignal<Post | null>(null);
  const [loading, setLoading] = createSignal(true);

  const handleBackClick = (e: MouseEvent) => {
    e.preventDefault();
    navigateToBlog();
  };

  createEffect(() => {
    const slug = props.slug;

    if (!slug) {
      console.log('No slug provided');
      setLoading(false);
      setPost(null);
      return;
    }

    setLoading(true);
    setPost(null);

    (async () => {
      try {
        console.log('Loading blog post:', slug);
        const response = await fetch(`/blog/${slug}.md`);
        console.log('Response status:', response.status, response.ok);

        if (response.ok) {
          const markdown = await response.text();
          console.log('Markdown loaded, length:', markdown.length);

          // Parse frontmatter (simple version)
          const lines = markdown.split('\n');
          let title = '';
          let date = '';
          let contentStart = 0;

          if (lines[0] === '---') {
            for (let i = 1; i < lines.length; i++) {
              if (lines[i] === '---') {
                contentStart = i + 1;
                break;
              }
              if (lines[i].startsWith('title:')) {
                title = lines[i].substring(6).trim();
              }
              if (lines[i].startsWith('date:')) {
                date = lines[i].substring(5).trim();
              }
            }
          }

          const content = lines.slice(contentStart).join('\n');
          const html = await marked.parse(content);

          console.log('Post parsed:', { title, date, contentLength: html.length });

          setPost({
            title: title || t('blog.notFound'),
            date: date || new Date().toLocaleDateString(language()),
            content: html
          });
        } else {
          console.error('Failed to load post, status:', response.status);
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
      } finally {
        setLoading(false);
      }
    })();
  });

  return (
    <section class="blog-post-section">
      <div class="container">
        <Show when={!loading()} fallback={<div class="blog-loading">{t('blog.loading')}</div>}>
          <Show when={post()} fallback={<div class="blog-error">{t('blog.notFound')}</div>}>
            <article class="blog-post">
              <header class="blog-post-header">
                <h1 class="blog-post-title">{post()!.title}</h1>
                <time class="blog-post-date">{post()!.date}</time>
              </header>
              <div class="blog-post-content" innerHTML={post()!.content} />
              <footer class="blog-post-footer">
                <a href="#" onClick={handleBackClick} class="blog-back-link">← {t('blog.backToBlog')}</a>
              </footer>
            </article>
          </Show>
        </Show>
      </div>
    </section>
  );
};

export default BlogPost;
